import { ISDUIResponsiveValue } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/Styling/responsiveValues";
import { PageParams } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/types";
import {
  getBatchedItemNodePropsLists,
  getComponentLayoutConfigsByViewport,
  getResponsiveSortedOrdersAndWidthsAndLayoutConfigs,
  getSortedItemsByOrderAndWidth,
  ItemNodeProps,
} from "@faire/deprecated-retailer-visitor-shared/components/SDUI/LayoutRenderers/FlowLayout/batchedFlowLayoutUtils";
import {
  getColSpanFromItemWidths,
  getItemOrders,
  getItemWidths,
  getItemWidthsFromViewportVisibility,
  getLCMOfNumColumnsGivenWidths,
  NUM_COLUMNS_GRID,
  NUM_COLUMNS_GRID_DESKTOP,
} from "@faire/deprecated-retailer-visitor-shared/components/SDUI/LayoutRenderers/FlowLayout/flowLayoutCalculationUtils";
import { getFlowLayoutGapStyles } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/LayoutRenderers/FlowLayout/getFlowLayoutGapStyles.css";
import { SDUIFlowLayoutItem } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/LayoutRenderers/FlowLayout/SDUIFlowLayoutItem";
import { SDUILayoutRendererItem } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/LayoutRenderers/types";
import { UPLTSetter } from "@faire/deprecated-retailer-visitor-shared/components/UPLT/UPLTSetter";
import {
  assignInlineVars,
  DynamicVariable,
} from "@faire/deprecated-retailer-visitor-shared/lib/tailwindUtils";
import { IFlowLayout } from "@faire/web-api--source/indigofair/data/IFlowLayout";
import { IResponsiveComponentLayoutConfig } from "@faire/web-api--source/indigofair/data/IResponsiveComponentLayoutConfig";
import { WebClientSize } from "@faire/web-api--source/indigofair/data/WebClientSize";
import { Viewport } from "@faire/web/common/hooks/viewport-utils/viewport-utils";
import { cn } from "@faire/web/tools/cn";
import React, { Suspense } from "react";

import { SDUIFlowLayoutItemOnVisibilityRendererProvider } from "./SDUIFlowLayoutItemOnVisibilityRendererContext";
import { SDUIFlowLayoutItemTopAnchorRenderer } from "./SDUIFlowLayoutItemTopAnchorRenderer";

const varMobileNumColumns: DynamicVariable<"--f-sdui-fl-nc-m"> = {
  variable: "--f-sdui-fl-nc-m",
  className: "grid-cols-(--f-sdui-fl-nc-m)",
};

const varTabletNumColumns: DynamicVariable<"--f-sdui-fl-nc-md"> = {
  variable: "--f-sdui-fl-nc-md",
  className: "md:grid-cols-(--f-sdui-fl-nc-md)",
};

const varDesktopNumColumns: DynamicVariable<"--f-sdui-fl-nc-lg"> = {
  variable: "--f-sdui-fl-nc-lg",
  className: "lg:grid-cols-(--f-sdui-fl-nc-lg)",
};

const varXLargeNumColumns: DynamicVariable<"--f-sdui-fl-nc-xl"> = {
  variable: "--f-sdui-fl-nc-xl",
  className: "xl:grid-cols-(--f-sdui-fl-nc-xl)",
};

export const SDUIFlowLayoutRenderer = ({
  flowLayout,
  items,
  viewport,
  shouldBatchRender = false,
  numFirstRows = 2,
  pageParams,
  shouldSetUPLTValues = false,
}: {
  flowLayout: IFlowLayout;
  items: Array<SDUILayoutRendererItem>;
  viewport?: Viewport;
  shouldBatchRender?: boolean;
  numFirstRows?: number;
  pageParams?: PageParams;
  shouldSetUPLTValues?: boolean;
}) => {
  let itemWidths: ISDUIResponsiveValue<(number | undefined)[]> | undefined =
    undefined;
  let itemOrders: ISDUIResponsiveValue<number[]> | undefined = undefined;
  let numColumnsPerRow: ISDUIResponsiveValue<number> | undefined = undefined;
  let sortedItems: Array<SDUILayoutRendererItem> = items;
  let componentLayoutConfigsByViewport: IResponsiveComponentLayoutConfig[] = [];

  if (flowLayout.component_widths) {
    itemWidths = getItemWidthsFromViewportVisibility(
      flowLayout.component_widths,
      items
    );
  } else if (flowLayout.responsive_component_layout_configs) {
    itemOrders = {
      mobile_and_above: getItemOrders(
        flowLayout.responsive_component_layout_configs.mobile_and_above,
        items.length
      ),
      tablet_and_above: getItemOrders(
        flowLayout.responsive_component_layout_configs.tablet_and_above,
        items.length
      ),
      desktop_and_above: getItemOrders(
        flowLayout.responsive_component_layout_configs.desktop_and_above,
        items.length
      ),
      x_large_and_above: getItemOrders(
        flowLayout.responsive_component_layout_configs.x_large_and_above,
        items.length
      ),
    };

    itemWidths = {
      mobile_and_above: getItemWidths(
        flowLayout.responsive_component_layout_configs.mobile_and_above,
        items.length
      ),
      tablet_and_above: getItemWidths(
        flowLayout.responsive_component_layout_configs.tablet_and_above,
        items.length
      ),
      desktop_and_above: getItemWidths(
        flowLayout.responsive_component_layout_configs.desktop_and_above,
        items.length
      ),
      x_large_and_above: getItemWidths(
        flowLayout.responsive_component_layout_configs.x_large_and_above,
        items.length
      ),
    };

    numColumnsPerRow = {
      mobile_and_above: getLCMOfNumColumnsGivenWidths(
        itemWidths.mobile_and_above ?? []
      ),
      tablet_and_above: getLCMOfNumColumnsGivenWidths(
        itemWidths.tablet_and_above ?? []
      ),
      desktop_and_above: getLCMOfNumColumnsGivenWidths(
        itemWidths.desktop_and_above ?? []
      ),
      x_large_and_above: getLCMOfNumColumnsGivenWidths(
        itemWidths.x_large_and_above ?? []
      ),
    };

    if (viewport && shouldBatchRender) {
      componentLayoutConfigsByViewport =
        getComponentLayoutConfigsByViewport(
          flowLayout.responsive_component_layout_configs,
          viewport
        ) ?? [];

      const itemOrdersByViewport = getItemOrders(
        componentLayoutConfigsByViewport,
        items.length
      );
      const itemWidthsByViewport = getItemWidths(
        componentLayoutConfigsByViewport,
        items.length
      );

      // Use the sorted items as our base items list
      sortedItems = getSortedItemsByOrderAndWidth(
        items,
        itemOrdersByViewport,
        itemWidthsByViewport
      );

      // Update item orders, widths, and the layout configs to match the sorted items
      const {
        responsiveSortedOrders,
        responsiveSortedWidths,
        responsiveSortedConfigs,
      } = getResponsiveSortedOrdersAndWidthsAndLayoutConfigs(
        sortedItems,
        items,
        itemOrders,
        itemWidths,
        flowLayout.responsive_component_layout_configs
      );
      itemOrders = responsiveSortedOrders;
      itemWidths = responsiveSortedWidths;

      componentLayoutConfigsByViewport =
        getComponentLayoutConfigsByViewport(
          responsiveSortedConfigs,
          viewport
        ) ?? [];
    }
  }

  const gapStyles = getFlowLayoutGapStyles(flowLayout);

  if (!itemWidths || sortedItems.length === 0) {
    return null;
  }

  // Row gap is rendered as padding-bottom of grid child items. This lets us
  // render invisible full-width items in the grid without affecting the layout.
  const itemNodePropsList = getFlowLayoutItemPropsList(
    sortedItems,
    itemWidths,
    itemOrders,
    numColumnsPerRow,
    gapStyles.rowSpacingClasses
  );

  const shouldUseBatchRendering = !!(viewport && shouldBatchRender);

  return (
    <MaybeSDUIFlowLayoutItemOnVisibilityRendererProvider
      shouldWrap={shouldUseBatchRendering}
    >
      <div
        data-test-id="flow-layout-container"
        className={cn(
          "grid w-full grid-flow-row",
          varMobileNumColumns.className,
          varTabletNumColumns.className,
          varDesktopNumColumns.className,
          varXLargeNumColumns.className,
          gapStyles.columnSpacingClasses
        )}
        style={assignInlineVars({
          [varMobileNumColumns.variable]: `repeat(${numColumnsPerRow?.mobile_and_above ?? NUM_COLUMNS_GRID}, 1fr)`,
          [varTabletNumColumns.variable]: `repeat(${numColumnsPerRow?.tablet_and_above ?? NUM_COLUMNS_GRID}, 1fr)`,
          [varDesktopNumColumns.variable]: `repeat(${numColumnsPerRow?.desktop_and_above ?? NUM_COLUMNS_GRID_DESKTOP}, 1fr)`,
          [varXLargeNumColumns.variable]: `repeat(${numColumnsPerRow?.x_large_and_above ?? NUM_COLUMNS_GRID_DESKTOP}, 1fr)`,
          ...gapStyles.columnSpacingVars,
          ...gapStyles.rowSpacingVars,
        })}
      >
        {shouldUseBatchRendering ? (
          <BatchRenderFlowLayoutItems
            componentLayoutConfigs={componentLayoutConfigsByViewport}
            itemNodePropsList={itemNodePropsList}
            numFirstRows={numFirstRows}
            pageParams={pageParams}
            shouldSetUPLTValues={shouldSetUPLTValues}
          />
        ) : (
          itemNodePropsList.map((itemNodeProps) =>
            renderFlowLayoutItem({ itemNodeProps })
          )
        )}
      </div>
    </MaybeSDUIFlowLayoutItemOnVisibilityRendererProvider>
  );
};

const MaybeSDUIFlowLayoutItemOnVisibilityRendererProvider = ({
  children,
  shouldWrap,
}: {
  children: React.ReactNode;
  shouldWrap: boolean;
}) => {
  if (shouldWrap) {
    return (
      <SDUIFlowLayoutItemOnVisibilityRendererProvider>
        <SDUIFlowLayoutItemTopAnchorRenderer />
        {children}
      </SDUIFlowLayoutItemOnVisibilityRendererProvider>
    );
  }
  return children;
};

const delayedComponents = async (
  componentPropsList: ItemNodeProps[],
  itemsPerColumn: number,
  pageParams?: PageParams
) => {
  await new Promise((resolve) => setTimeout(resolve, 5));
  return componentPropsList.map((itemNodeProps, index) => {
    const flagIndex = Math.floor(index / itemsPerColumn);
    const shouldUpdateFlagOnVisibility = index % itemsPerColumn === 0;
    return renderFlowLayoutItem({
      itemNodeProps,
      pageLoadKey: pageParams?.uniquePageKey ?? "",
      shouldRenderOnVisibility: true,
      visibilityFlagIndex: flagIndex,
      shouldUpdateFlagOnVisibility,
    });
  });
};

const BatchRenderFlowLayoutItems = ({
  componentLayoutConfigs,
  itemNodePropsList,
  numFirstRows,
  pageParams,
  shouldSetUPLTValues,
}: {
  componentLayoutConfigs: IResponsiveComponentLayoutConfig[];
  itemNodePropsList: ItemNodeProps[];
  numFirstRows: number;
  pageParams?: PageParams;
  shouldSetUPLTValues?: boolean;
}) => {
  const { firstRowsItemNodePropsList, restItemNodePropsList } =
    getBatchedItemNodePropsLists(
      componentLayoutConfigs,
      itemNodePropsList,
      numFirstRows
    );
  return (
    <>
      {shouldSetUPLTValues ? (
        <UPLTSetter
          numComponentsPerClientSize={{
            [WebClientSize.MOBILE]: firstRowsItemNodePropsList.length,
            [WebClientSize.TABLET]: firstRowsItemNodePropsList.length,
            [WebClientSize.DESKTOP]: firstRowsItemNodePropsList.length,
            [WebClientSize.XLARGE_DESKTOP]: firstRowsItemNodePropsList.length,
            [WebClientSize.WEB_CLIENT_SIZE_UNKNOWN]:
              firstRowsItemNodePropsList.length,
          }}
          ignoreImages={
            firstRowsItemNodePropsList.length === 0 ||
            pageParams?.shouldIgnoreImagesForUPLT
          }
        />
      ) : null}
      {firstRowsItemNodePropsList.map((itemNodeProps) =>
        renderFlowLayoutItem({
          itemNodeProps,
          pageLoadKey: pageParams?.uniquePageKey ?? "",
          shouldRenderOnVisibility: false,
          visibilityFlagIndex: undefined,
          shouldUpdateFlagOnVisibility: false,
          shouldAttachUPLTRef: true,
        })
      )}
      {restItemNodePropsList.length > 0 ? (
        <Suspense
          fallback={null}
          key={`flow-layout-rest-batch-${pageParams?.uniquePageKey}`}
        >
          {delayedComponents(
            restItemNodePropsList,
            Math.floor(firstRowsItemNodePropsList.length / numFirstRows),
            pageParams
          )}
        </Suspense>
      ) : null}
    </>
  );
};

const renderFlowLayoutItem = ({
  itemNodeProps,
  pageLoadKey,
  shouldRenderOnVisibility,
  visibilityFlagIndex,
  shouldUpdateFlagOnVisibility,
  shouldAttachUPLTRef,
}: {
  itemNodeProps: ItemNodeProps;
  pageLoadKey?: string;
  shouldRenderOnVisibility?: boolean;
  visibilityFlagIndex?: number;
  shouldUpdateFlagOnVisibility?: boolean;
  shouldAttachUPLTRef?: boolean;
}) => {
  return (
    <SDUIFlowLayoutItem
      key={itemNodeProps.key}
      itemColSpans={itemNodeProps.itemColSpans}
      item={itemNodeProps.item}
      itemOrder={itemNodeProps.itemOrder}
      shouldRenderOnVisibility={shouldRenderOnVisibility}
      visibilityFlagIndex={visibilityFlagIndex}
      shouldUpdateFlagOnVisibility={shouldUpdateFlagOnVisibility}
      shouldAttachUPLTRef={shouldAttachUPLTRef}
      pageLoadKey={pageLoadKey}
      enableLoadingPlaceholder={itemNodeProps.enableLoadingPlaceholder}
      rowSpacingClasses={itemNodeProps.rowSpacingClasses}
    />
  );
};

const getFlowLayoutItemPropsList = (
  items: SDUILayoutRendererItem[],
  itemWidths: ISDUIResponsiveValue<(number | undefined)[]>,
  itemOrders: ISDUIResponsiveValue<number[]> | undefined,
  numColumnsPerRow?: ISDUIResponsiveValue<number>,
  rowSpacingClasses?: string[]
) => {
  const { itemNodePropsList } = items.reduce((accValue, item, index) => {
    const { colSpan } = getColSpanFromItemWidths(
      itemWidths,
      index,
      numColumnsPerRow
    );

    let itemOrder: ISDUIResponsiveValue<number> | undefined = undefined;
    if (itemOrders) {
      itemOrder = {
        mobile_and_above: itemOrders.mobile_and_above?.[index],
        tablet_and_above: itemOrders.tablet_and_above?.[index],
        desktop_and_above: itemOrders.desktop_and_above?.[index],
        x_large_and_above: itemOrders.x_large_and_above?.[index],
      };
    }

    const itemNodeProps: ItemNodeProps = {
      key: item.itemKey,
      itemColSpans: colSpan,
      item: item,
      itemOrder: itemOrder,
      enableLoadingPlaceholder: item.enableLoadingPlaceholder,
      rowSpacingClasses: item.isInvisibleSpacerForFlowLayoutAssignment
        ? undefined
        : rowSpacingClasses,
    };

    return {
      itemNodePropsList: [...accValue.itemNodePropsList, itemNodeProps],
    };
  }, INITIAL_ACC_VALUE);

  return itemNodePropsList;
};

const INITIAL_ACC_VALUE: {
  itemNodePropsList: ItemNodeProps[];
} = {
  itemNodePropsList: [],
};
